import React, { FC, FormEvent, useEffect, useState } from 'react';
import FormAddressInput from './FormAddressInput';
import { Pays, SearchCompany } from '../../../interfaces-base';
import { countryNeedSiret, countryHasRequiredSiret, countryHasNumTvaIntra, countryHasRequiredNumTvaIntra, prettifySiret } from '../utils/form.util';
import { AddressType, FetchInfos, FormKeys, ProForm } from '../fa-interfaces';
import FormAddressAutocomplete from './FormAddressAutocomplete';
import { fetchSearchCompany } from '../../../service/address.api';
import { TranslationProps } from '../../../contexts/TranslationContext';
import './css/section-pro.css';

interface Props {
  addressType: AddressType;
  state: ProForm;
  country: Pays;
  errorList: Partial<Record<FormKeys, undefined | string>>;
  tvaFetchState: FetchInfos;
  modeReduce?: boolean;
  zipCode?: string;
  onChange: (field: keyof ProForm, value: string) => void;
}

const SectionPro: FC<Props & TranslationProps> = ({ textsContent, addressType, state, country, errorList, tvaFetchState, modeReduce, zipCode, onChange }): JSX.Element => {
  const [showSiret, setShowSiret] = useState<boolean>(false);

  useEffect(() => {
    const canShowSiret = countryNeedSiret(country, zipCode);
    setShowSiret(canShowSiret);
    if (!canShowSiret) {
      onChange('siret', '');
    }
  }, [zipCode, country]);

  const getTVALabel = (): string => {
    const name = country.codePaysTva === 'CH' ? 'SHIPPING_FORM_TVA_CH' : 'SHIPPING_FORM_TVA';
    const label = textsContent?.[name] || 'N° UID';
    return `${label}${countryHasRequiredNumTvaIntra(country.idPays, addressType) ? ' *' : ''}`;
  };

  const requestSearchCompany = async (search: string, signal: AbortSignal, cleanFn: () => void): Promise<JSX.Element[]> => {
    return fetchSearchCompany(search, 10, signal)
      .then((companies) => {
        const list = companies
          .map((company) => (
            <div key={company.siret} className="fa-company-item" onClick={() => handleClickCompany(company, cleanFn)}>
              <span className="fa-company-name">{company.company}</span>
              <span className="fa-company-grey">{company.address}, {company.zipCode} {company.city}</span>
            </div>
          ));
        list.length > 0 && list.push(<div key="fa-company-not-found" className="fa-company-item" onClick={cleanFn}>{textsContent?.['SHIPPING_FORM_COMPANY_NOT_IN_LIST']}</div>);
        return list;
      })
      .catch(() => []);
  };

  const handleClickCompany = (company: SearchCompany, cleanFn: () => void): void => {
    onChange('siret', prettifySiret(company.siret));
    onChange('societe', company.company);
    onChange('code_ape', company.apeCode);
    onChange('categorie_juridique', company.legalCategory);
    cleanFn();
  };

  const defaultHandleChange = (key: keyof ProForm) => (event: FormEvent<HTMLInputElement>) => {
    const { target } = event;
    if (target instanceof HTMLInputElement) {
      onChange(key, target.value);
    }
  };

  return (
    <div className={`fa-section-pro-container ${modeReduce && 'reduce'}`}>
      <FormAddressAutocomplete
        label={textsContent?.['SHIPPING_FORM_COMPANY_V2']}
        value={state.societe || ''}
        errorMessage={errorList['societe']}
        request={countryNeedSiret(country, zipCode) ? requestSearchCompany : undefined}
        name="societe"
        onChange={defaultHandleChange('societe')}
      />
      {showSiret && (
        <FormAddressInput
          label={`${textsContent?.['SHIPPING_FORM_SIRET']}${countryHasRequiredSiret(country, addressType, zipCode) ? ' *' : ''}`}
          value={state.siret || ''}
          errorMessage={errorList['siret']}
          name="siret"
          onChange={defaultHandleChange('siret')}
        />
      )}
      {countryHasNumTvaIntra(country.idPays) && (
        <FormAddressInput
          label={getTVALabel()}
          value={state.numero_tva || ''}
          prependItem={<span className="fa-prepend-tva">{country.codePaysTva}</span>}
          errorMessage={errorList['numero_tva']}
          loading={tvaFetchState.loading}
          name="numero_tva"
          onChange={defaultHandleChange('numero_tva')}
        />
      )}
    </div>
  );
};

export default SectionPro;
